import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/lib/auth';
import { Instagram, Phone, MessageSquare, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { MetaTags } from '@/components/MetaTags';

interface RegistrationFormData {
  instagram: string;
  referral: string;
  phone: string;
  preferredComm: string[];
  joinTithorea: boolean;
}

export function Azores() {
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<RegistrationFormData>({
    instagram: '',
    referral: '',
    phone: '',
    preferredComm: ['email'],
    joinTithorea: false
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Preload video data
    video.preload = "auto";

    // Function to attempt playback
    const attemptPlay = async () => {
      try {
        await video.play();
        setIsPlaying(true);
      } catch (error) {
        console.warn('Autoplay failed:', error);
        setIsPlaying(false);
      }
    };

    // Handle video loading states
    const handleLoadedMetadata = () => {
      setIsVideoLoaded(true);
      attemptPlay();
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        attemptPlay();
      } else {
        video.pause();
        setIsPlaying(false);
      }
    };

    // Add event listeners
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));

    // Start loading the video
    video.load();

    // Cleanup
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
    };
  }, []);

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(error => {
          console.error('Play failed:', error);
          setIsPlaying(false);
        });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const toggleCommunicationMethod = (method: string) => {
    setFormData(prev => {
      const currentMethods = prev.preferredComm;
      const newMethods = currentMethods.includes(method)
        ? currentMethods.filter(m => m !== method)
        : [...currentMethods, method];
      return {
        ...prev,
        preferredComm: newMethods.length > 0 ? newMethods : ['email'] // Ensure at least one method is selected
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Insert directly using Supabase client
      const { error: insertError } = await supabase
        .from('registrations')
        .insert({
          user_id: user.id,
          instagram: formData.instagram,
          referral: formData.referral,
          phone: formData.phone,
          preferred_comm: formData.preferredComm,
          join_tithorea: formData.joinTithorea
        });

      if (insertError) throw insertError;

      alert('Thank you for registering! We will be in touch soon.');
      setShowRegistration(false);
    } catch (error) {
      console.error('Failed to submit registration:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags 
        title="Discover Pico Island - rendezvous"
        description="A journey to the hidden gem of the Azores. Join us in Pico Island, May 29 - June 2."
        image="https://rendezvouswebsite.b-cdn.net/meta/azores-preview.jpg"
        url="https://letsrendezvous.co/azores"
      />

      {/* Video Hero Section */}
      <div className="relative h-screen w-full overflow-hidden">
        {/* Video Container */}
        <div className="absolute inset-0">
          {/* Fallback image shown until video is loaded */}
          <div
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${
              isVideoLoaded ? 'opacity-0' : 'opacity-100'
            }`}
            style={{
              backgroundImage:
                'url(https://rendezvouswebsite.b-cdn.net/Azores/Azores%20Still.png)',
            }}
          />

          <video
            ref={videoRef}
            playsInline
            loop
            muted={isMuted}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              isVideoLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={() => setShowControls(true)}
          >
            <source
              src="https://rendezvouswebsite.b-cdn.net/Azores/Sequence%2001.mp4"
              type="video/mp4"
            />
            Your browser doesn't support HTML5 video.
          </video>

          {/* Video Controls */}
          <div 
            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-4 px-6 py-3 bg-earth-900/90 backdrop-blur-sm rounded-full shadow-lg"
          >
            <button
              onClick={handlePlayPause}
              className="text-sand-100 hover:text-sand-200 transition-colors p-2 rounded-full hover:bg-earth-800/50"
              aria-label={isPlaying ? 'Pause video' : 'Play video'}
            >
              {isPlaying ? (
                <Pause className="h-6 w-6" />
              ) : (
                <Play className="h-6 w-6" />
              )}
            </button>
            <div className="w-px h-6 bg-earth-700" />
            <button
              onClick={handleToggleMute}
              className="text-sand-100 hover:text-sand-200 transition-colors p-2 rounded-full hover:bg-earth-800/50"
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            >
              {isMuted ? (
                <VolumeX className="h-6 w-6" />
              ) : (
                <Volume2 className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display text-sand-100 mb-6">
              Discover Pico Island
            </h1>
            <p className="text-xl md:text-2xl text-sand-200 mb-8">
              A journey to the hidden gem of the Azores
            </p>
            {!user ? (
              <Button 
                size="lg"
                onClick={() => setShowAuthModal(true)}
                className="text-lg px-8 py-4"
              >
                Sign Up to Learn More
              </Button>
            ) : (
              <Button 
                size="lg"
                onClick={() => setShowRegistration(true)}
                className="text-lg px-8 py-4"
              >
                Register for Early Access
              </Button>
            )}
          </motion.div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
      />

      {/* Registration Modal */}
      {showRegistration && (
        <div className="fixed inset-0 bg-earth-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-earth-800 rounded-lg p-6 w-full max-w-md"
          >
            <h2 className="text-2xl font-display mb-6">Register for Early Access</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-sand-300 mb-2">
                  Instagram Handle
                </label>
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-sand-400" />
                  <input
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400"
                    placeholder="@username"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-sand-300 mb-2">
                  Who Referred You?
                </label>
                <input
                  type="text"
                  value={formData.referral}
                  onChange={(e) => setFormData(prev => ({ ...prev, referral: e.target.value }))}
                  className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-sand-300 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-sand-400" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400"
                    placeholder="+1 (555) 123-4567"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-sand-300 mb-2">
                  Preferred Communication Methods
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['email', 'sms', 'whatsapp'].map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => toggleCommunicationMethod(method)}
                      className={`px-4 py-2 text-sm border ${
                        formData.preferredComm.includes(method)
                          ? 'border-sand-400 bg-sand-400/10 text-sand-100'
                          : 'border-earth-700 text-sand-400 hover:border-sand-400'
                      }`}
                    >
                      {method.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.joinTithorea}
                    onChange={(e) => setFormData(prev => ({ ...prev, joinTithorea: e.target.checked }))}
                    className="rounded bg-earth-700 border-earth-600 text-sand-500 focus:ring-sand-400"
                  />
                  <span className="text-sm text-sand-300">
                    I'm interested in joining Tithorea in Jordan the weekend before
                  </span>
                </label>
              </div>

              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}

              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowRegistration(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}